import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../utils/api'
import {
  ClockIcon,
  EmailIcon,
  PhoneIcon,
  BellIcon,
  TaskIcon,
  PlusIcon,
  TrashIcon,
  SaveIcon,
  XIcon,
  SettingsIcon,
  CheckCircleIcon
} from './Icons'

// Enhanced interfaces for the new automation system
interface EnhancedWorkflowAutomation {
  id?: number
  workflow_step_id: number
  trigger_event: string
  action_type: 'send_email' | 'send_sms' | 'create_task' | 'send_notification' | 'update_status' | 'log_activity' | 'assign_deal' | 'external_api_call' | 'set_reminder' | 'update_financial'
  action_config: any
  delay_hours: number
  is_active: boolean
  
  // New trigger fields
  time_delay_value?: number
  time_delay_unit?: 'minutes' | 'hours' | 'days' | 'weeks' | 'months'
  field_trigger_name?: string
  field_trigger_condition?: 'changed' | 'increased' | 'decreased' | 'equals' | 'greater_than' | 'less_than'
  field_trigger_value?: string
  user_action_type?: 'comment_added' | 'deal_assigned' | 'status_changed_manually' | 'file_uploaded'
  external_api_endpoint?: string
  external_api_config?: any
  task_template_id?: number
  conditions?: WorkflowCondition[]
  
  // New action-specific fields
  assign_to_user_id?: number
  external_api_method?: string
  external_api_headers?: string
  external_api_body?: string
  reminder_type?: string
  reminder_hours_before?: number
  financial_field?: string
  financial_operation?: string
  financial_value?: string
  activity_log_template?: string
}

interface WorkflowCondition {
  id?: number
  field_name: string
  operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'starts_with' | 'ends_with' | 'is_empty' | 'is_not_empty'
  value: string
  logical_operator: 'AND' | 'OR'
  condition_group: number
}

interface TaskTemplate {
  id: number
  name: string
  description?: string
  deadline_value: number
  deadline_unit: 'days' | 'weeks' | 'months' | 'years'
  priority: 'low' | 'medium' | 'high' | 'urgent'
}

interface NotificationTemplate {
  id: number
  name: string
  template_type: 'email' | 'sms' | 'internal'
  subject?: string
  content: string
}

interface DealField {
  field: string
  label: string
  label_fr: string
  type: string
  required_by_default: boolean
}

interface EnhancedAutomationRuleBuilderProps {
  stepId: number
  existingRules: EnhancedWorkflowAutomation[]
  existingRequiredFields?: string[]
  onSave: (rules: EnhancedWorkflowAutomation[], requiredFields: string[]) => Promise<void>
  onCancel: () => void
  notificationTemplates: NotificationTemplate[]
}

const EnhancedAutomationRuleBuilder: React.FC<EnhancedAutomationRuleBuilderProps> = ({
  stepId,
  existingRules,
  existingRequiredFields = [],
  onSave,
  onCancel,
  notificationTemplates
}) => {
  const { t, i18n } = useTranslation()
  const [activeTab, setActiveTab] = useState<'rules' | 'validation'>('validation')
  const [rules, setRules] = useState<EnhancedWorkflowAutomation[]>(existingRules)
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([])
  const [dealFields, setDealFields] = useState<DealField[]>([])
  const [requiredFields, setRequiredFields] = useState<string[]>(existingRequiredFields)
  const [showTaskTemplateEditor, setShowTaskTemplateEditor] = useState(false)
  const [editingTaskTemplate, setEditingTaskTemplate] = useState<Partial<TaskTemplate>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchTaskTemplates()
    fetchDealFields()
    fetchRequiredFields()
  }, [stepId])

  const fetchTaskTemplates = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/task-templates?step_id=${stepId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setTaskTemplates(data.templates || [])
      }
    } catch (error) {
      console.error('Error fetching task templates:', error)
    }
  }

  const fetchDealFields = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/deal-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setDealFields(data.fields || [])
      }
    } catch (error) {
      console.error('Error fetching deal fields:', error)
    }
  }

  const fetchRequiredFields = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/steps/${stepId}/required-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setRequiredFields(data.required_fields || [])
      }
    } catch (error) {
      console.error('Error fetching required fields:', error)
    }
  }

  const toggleRequiredField = (field: string) => {
    setRequiredFields(prev => {
      if (prev.includes(field)) {
        return prev.filter(f => f !== field)
      } else {
        return [...prev, field]
      }
    })
  }

  const getFieldLabel = (field: DealField) => {
    return i18n.language === 'fr' ? field.label_fr : field.label
  }

  const addRule = () => {
    const newRule: EnhancedWorkflowAutomation = {
      workflow_step_id: stepId,
      trigger_event: 'on_enter',
      action_type: 'send_email',
      action_config: {},
      delay_hours: 0,
      is_active: true,
      conditions: []
    }
    setRules([...rules, newRule])
  }

  const updateRule = (index: number, updates: Partial<EnhancedWorkflowAutomation>) => {
    const updatedRules = [...rules]
    updatedRules[index] = { ...updatedRules[index], ...updates }
    setRules(updatedRules)
  }

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index))
  }

  const addCondition = (ruleIndex: number) => {
    const newCondition: WorkflowCondition = {
      field_name: 'amount',
      operator: 'equals',
      value: '',
      logical_operator: 'AND',
      condition_group: 1
    }
    
    const updatedRules = [...rules]
    updatedRules[ruleIndex].conditions = [...(updatedRules[ruleIndex].conditions || []), newCondition]
    setRules(updatedRules)
  }

  const updateCondition = (ruleIndex: number, conditionIndex: number, updates: Partial<WorkflowCondition>) => {
    const updatedRules = [...rules]
    if (updatedRules[ruleIndex].conditions) {
      updatedRules[ruleIndex].conditions![conditionIndex] = {
        ...updatedRules[ruleIndex].conditions![conditionIndex],
        ...updates
      }
      setRules(updatedRules)
    }
  }

  const removeCondition = (ruleIndex: number, conditionIndex: number) => {
    const updatedRules = [...rules]
    if (updatedRules[ruleIndex].conditions) {
      updatedRules[ruleIndex].conditions = updatedRules[ruleIndex].conditions!.filter((_, i) => i !== conditionIndex)
      setRules(updatedRules)
    }
  }

  const createTaskTemplate = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/task-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...editingTaskTemplate,
          workflow_step_id: stepId
        })
      })

      if (response.ok) {
        const data = await response.json()
        setTaskTemplates([...taskTemplates, data.template])
        setShowTaskTemplateEditor(false)
        setEditingTaskTemplate({})
      }
    } catch (error) {
      console.error('Error creating task template:', error)
    }
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      await onSave(rules, requiredFields)
    } finally {
      setLoading(false)
    }
  }

  const triggerEventOptions = [
    { value: 'on_enter', label: 'When entering this step' },
    { value: 'on_exit', label: 'When leaving this step' },
    { value: 'field_update', label: 'When deal field changes' },
    { value: 'time_based', label: 'Time-based trigger' },
    { value: 'user_action', label: 'User action trigger' },
    { value: 'external_api', label: 'External API trigger' }
  ]

  const actionTypeOptions = [
    { value: 'send_email', label: 'Send Email', icon: EmailIcon },
    { value: 'send_sms', label: 'Send SMS', icon: PhoneIcon },
    { value: 'create_task', label: 'Create Task', icon: TaskIcon },
    { value: 'send_notification', label: 'Generate Internal Alert', icon: BellIcon },
    { value: 'update_status', label: 'Update Deal Status Automatically', icon: SettingsIcon },
    { value: 'log_activity', label: 'Log Activity', icon: SettingsIcon },
    { value: 'assign_deal', label: 'Assign Deal to User', icon: SettingsIcon },
    { value: 'external_api_call', label: 'Trigger External API Call', icon: SettingsIcon },
    { value: 'set_reminder', label: 'Set Reminder', icon: ClockIcon },
    { value: 'update_financial', label: 'Update Financial Metrics', icon: SettingsIcon }
  ]

  const fieldOptions = [
    { value: 'amount', label: 'Deal Amount' },
    { value: 'net_commission', label: 'Net Commission' },
    { value: 'expected_close', label: 'Expected Close Date' },
    { value: 'status_id', label: 'Status' },
    { value: 'customer_name', label: 'Customer Name' }
  ]

  const conditionOperators = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'contains', label: 'Contains' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' }
  ]

  const userActionTypes = [
    { value: 'comment_added', label: 'Comment Added' },
    { value: 'deal_assigned', label: 'Deal Assigned' },
    { value: 'status_changed_manually', label: 'Status Changed Manually' },
    { value: 'file_uploaded', label: 'File Uploaded' }
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-md">
              <SettingsIcon className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              🤖 Enhanced Automation Rules
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowTaskTemplateEditor(true)}
              className="px-3 py-1.5 text-sm bg-gradient-to-r from-indigo-500 to-gray-900 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all shadow-sm hover:shadow-md flex items-center space-x-1 font-medium"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Task Template</span>
            </button>
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('validation')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'validation'
                ? 'border-gray-900 dark:border-gray-400 text-gray-900 dark:text-white'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <CheckCircleIcon className="h-4 w-4" />
              <span>{t('settings.workflowManagement.validation.title', 'Validation')}</span>
              {requiredFields.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                  {requiredFields.length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'rules'
                ? 'border-gray-900 dark:border-gray-400 text-gray-900 dark:text-white'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <SettingsIcon className="h-4 w-4" />
              <span>{t('settings.workflowManagement.automationRules.title', 'Automation Rules')}</span>
              {rules.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
                  {rules.length}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
          {/* Validation Tab */}
          {activeTab === 'validation' && (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                  {t('settings.workflowManagement.validation.description', 'Required Fields for Status Transition')}
                </h4>
                <p className="text-xs text-blue-600 dark:text-blue-300">
                  {t('settings.workflowManagement.validation.helpText', 'Select which deal fields must be filled before a deal can transition to this status. These validations will be enforced when changing deal status.')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {dealFields.map((field) => (
                  <label
                    key={field.field}
                    className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      requiredFields.includes(field.field)
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                        : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={requiredFields.includes(field.field)}
                      onChange={() => toggleRequiredField(field.field)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-3"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {getFieldLabel(field)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          field.type === 'string' ? 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300' :
                          field.type === 'number' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' :
                          field.type === 'date' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300' :
                          field.type === 'reference' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300' :
                          'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                        }`}>
                          {field.type}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {field.field}
                      </span>
                    </div>
                    {requiredFields.includes(field.field) && (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2 flex-shrink-0" />
                    )}
                  </label>
                ))}
              </div>

              {requiredFields.length > 0 && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center space-x-2 text-green-800 dark:text-green-200">
                    <CheckCircleIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {t('settings.workflowManagement.validation.selectedFields', '{{count}} fields required', { count: requiredFields.length })}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {requiredFields.map(field => {
                      const fieldDef = dealFields.find(f => f.field === field)
                      return (
                        <span
                          key={field}
                          className="inline-flex items-center px-2 py-1 text-xs bg-white dark:bg-gray-800 text-green-700 dark:text-green-300 rounded-md border border-green-300 dark:border-green-700"
                        >
                          {fieldDef ? getFieldLabel(fieldDef) : field}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rules Tab */}
          {activeTab === 'rules' && (
          <div className="space-y-4">
            {rules.map((rule, ruleIndex) => (
              <div key={ruleIndex} className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl p-5 space-y-4 border-2 border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-all">
                {/* Rule Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md">
                      {ruleIndex + 1}
                    </div>
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                      Rule {ruleIndex + 1}
                    </h4>
                  </div>
                  <button
                    onClick={() => removeRule(ruleIndex)}
                    className="p-2 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Trigger Configuration */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Trigger Event
                    </label>
                    <select
                      value={rule.trigger_event}
                      onChange={(e) => updateRule(ruleIndex, { trigger_event: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    >
                      {triggerEventOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Action Type
                    </label>
                    <select
                      value={rule.action_type}
                      onChange={(e) => updateRule(ruleIndex, { action_type: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    >
                      {actionTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Trigger-specific Configuration */}
                {rule.trigger_event === 'field_update' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Field to Monitor
                      </label>
                      <select
                        value={rule.field_trigger_name || ''}
                        onChange={(e) => updateRule(ruleIndex, { field_trigger_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      >
                        <option value="">Select field...</option>
                        {fieldOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Condition
                      </label>
                      <select
                        value={rule.field_trigger_condition || ''}
                        onChange={(e) => updateRule(ruleIndex, { field_trigger_condition: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      >
                        <option value="changed">Changed</option>
                        <option value="increased">Increased</option>
                        <option value="decreased">Decreased</option>
                        <option value="equals">Equals</option>
                        <option value="greater_than">Greater Than</option>
                        <option value="less_than">Less Than</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Reference Value
                      </label>
                      <input
                        type="text"
                        value={rule.field_trigger_value || ''}
                        onChange={(e) => updateRule(ruleIndex, { field_trigger_value: e.target.value })}
                        placeholder="Value to compare against..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      />
                    </div>
                  </div>
                )}

                {rule.trigger_event === 'time_based' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        ⏰ Delay Amount
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={rule.time_delay_value || 1}
                        onChange={(e) => updateRule(ruleIndex, { time_delay_value: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Time Unit
                      </label>
                      <select
                        value={rule.time_delay_unit || 'days'}
                        onChange={(e) => updateRule(ruleIndex, { time_delay_unit: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                  </div>
                )}

                {rule.trigger_event === 'user_action' && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      👤 User Action Type
                    </label>
                    <select
                      value={rule.user_action_type || ''}
                      onChange={(e) => updateRule(ruleIndex, { user_action_type: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    >
                      <option value="">Select action...</option>
                      {userActionTypes.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Action Configuration Forms */}
                
                {/* Email Configuration */}
                {rule.action_type === 'send_email' && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">📧 Email Configuration</h5>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={rule.action_config?.to || ''}
                        onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, to: e.target.value } })}
                        placeholder="Recipient (customer, broker, or email)"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <input
                        type="text"
                        value={rule.action_config?.subject || ''}
                        onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, subject: e.target.value } })}
                        placeholder="Email subject"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <textarea
                      value={rule.action_config?.content || ''}
                      onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, content: e.target.value } })}
                      placeholder="Email content with {{variables}} support..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* SMS Configuration */}
                {rule.action_type === 'send_sms' && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">📱 SMS Configuration</h5>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={rule.action_config?.to || ''}
                        onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, to: e.target.value } })}
                        placeholder="Recipient (customer, broker, or phone)"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <textarea
                      value={rule.action_config?.content || ''}
                      onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, content: e.target.value } })}
                      placeholder="SMS message with {{variables}} support..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Task Template Selection for Task Actions */}
                {rule.action_type === 'create_task' && (
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      📋 Task Template
                    </label>
                    <select
                      value={rule.task_template_id || ''}
                      onChange={(e) => updateRule(ruleIndex, { task_template_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    >
                      <option value="">Create simple task...</option>
                      {taskTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} ({template.deadline_value} {template.deadline_unit})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Internal Alert Configuration */}
                {rule.action_type === 'send_notification' && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">🔔 Internal Alert Configuration</h5>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={rule.action_config?.title || ''}
                        onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, title: e.target.value } })}
                        placeholder="Alert title"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <select
                        value={rule.action_config?.priority || 'normal'}
                        onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, priority: e.target.value } })}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="low">Low Priority</option>
                        <option value="normal">Normal Priority</option>
                        <option value="high">High Priority</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                    <textarea
                      value={rule.action_config?.message || ''}
                      onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, message: e.target.value } })}
                      placeholder="Alert message with {{variables}} support..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Status Update Configuration */}
                {rule.action_type === 'update_status' && (
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">🔄 Status Update Configuration</h5>
                    <div className="grid grid-cols-1 gap-3">
                      <input
                        type="number"
                        value={rule.action_config?.status_id || ''}
                        onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, status_id: parseInt(e.target.value) } })}
                        placeholder="Target Status ID"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Enter the ID of the status you want to move the deal to
                      </p>
                    </div>
                  </div>
                )}

                {/* Activity Log Configuration */}
                {rule.action_type === 'log_activity' && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">📝 Activity Log Configuration</h5>
                    <textarea
                      value={rule.activity_log_template || ''}
                      onChange={(e) => updateRule(ruleIndex, { activity_log_template: e.target.value })}
                      placeholder="Activity log message template with {{variables}} support..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Deal Assignment Configuration */}
                {rule.action_type === 'assign_deal' && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">👤 Deal Assignment Configuration</h5>
                    <div className="grid grid-cols-1 gap-3">
                      <input
                        type="number"
                        value={rule.assign_to_user_id || ''}
                        onChange={(e) => updateRule(ruleIndex, { assign_to_user_id: parseInt(e.target.value) })}
                        placeholder="User ID to assign deal to"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Enter the user ID of the broker/user to assign this deal to
                      </p>
                    </div>
                  </div>
                )}

                {/* External API Configuration */}
                {rule.action_type === 'external_api_call' && (
                  <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">🌐 External API Configuration</h5>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                      <select
                        value={rule.external_api_method || 'POST'}
                        onChange={(e) => updateRule(ruleIndex, { external_api_method: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                      <input
                        type="url"
                        value={rule.external_api_endpoint || ''}
                        onChange={(e) => updateRule(ruleIndex, { external_api_endpoint: e.target.value })}
                        placeholder="API endpoint URL"
                        className="lg:col-span-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <textarea
                      value={rule.external_api_headers || ''}
                      onChange={(e) => updateRule(ruleIndex, { external_api_headers: e.target.value })}
                      placeholder='Headers (JSON format): {"Authorization": "Bearer token"}'
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <textarea
                      value={rule.external_api_body || ''}
                      onChange={(e) => updateRule(ruleIndex, { external_api_body: e.target.value })}
                      placeholder='Request body template with {{variables}}: {"deal_id": {{deal_id}}, "status": "{{status_name}}"}'
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Reminder Configuration */}
                {rule.action_type === 'set_reminder' && (
                  <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">⏰ Reminder Configuration</h5>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                      <select
                        value={rule.reminder_type || 'notification'}
                        onChange={(e) => updateRule(ruleIndex, { reminder_type: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="notification">In-app Notification</option>
                        <option value="email">Email Reminder</option>
                        <option value="sms">SMS Reminder</option>
                        <option value="task">Create Reminder Task</option>
                      </select>
                      <input
                        type="number"
                        value={rule.reminder_hours_before || 24}
                        onChange={(e) => updateRule(ruleIndex, { reminder_hours_before: parseInt(e.target.value) })}
                        placeholder="Hours before"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <input
                        type="text"
                        value={rule.action_config?.title || ''}
                        onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, title: e.target.value } })}
                        placeholder="Reminder title"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <textarea
                      value={rule.action_config?.message || ''}
                      onChange={(e) => updateRule(ruleIndex, { action_config: { ...rule.action_config, message: e.target.value } })}
                      placeholder="Reminder message with {{variables}} support..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Financial Update Configuration */}
                {rule.action_type === 'update_financial' && (
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">💰 Financial Update Configuration</h5>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                      <select
                        value={rule.financial_field || 'net_commission'}
                        onChange={(e) => updateRule(ruleIndex, { financial_field: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="net_commission">Net Commission</option>
                        <option value="amount">Deal Amount</option>
                        <option value="agency_fee">Agency Fee</option>
                      </select>
                      <select
                        value={rule.financial_operation || 'set'}
                        onChange={(e) => updateRule(ruleIndex, { financial_operation: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="set">Set to Value</option>
                        <option value="add">Add Value</option>
                        <option value="subtract">Subtract Value</option>
                        <option value="multiply">Multiply by Value</option>
                        <option value="percentage">Percentage of Current</option>
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        value={rule.financial_value || ''}
                        onChange={(e) => updateRule(ruleIndex, { financial_value: e.target.value })}
                        placeholder="Value/Amount"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Automatically calculate and update financial metrics based on the operation
                    </p>
                  </div>
                )}

                {/* Conditions */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Additional Conditions
                    </h5>
                    <button
                      onClick={() => addCondition(ruleIndex)}
                      className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors flex items-center space-x-1"
                    >
                      <PlusIcon className="h-3 w-3" />
                      <span>Add Condition</span>
                    </button>
                  </div>

                  {rule.conditions?.map((condition, conditionIndex) => (
                    <div key={conditionIndex} className="grid grid-cols-1 lg:grid-cols-4 gap-3 p-3 bg-gray-100 dark:bg-gray-600/50 rounded-lg">
                      <select
                        value={condition.field_name}
                        onChange={(e) => updateCondition(ruleIndex, conditionIndex, { field_name: e.target.value })}
                        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {fieldOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={condition.operator}
                        onChange={(e) => updateCondition(ruleIndex, conditionIndex, { operator: e.target.value as any })}
                        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {conditionOperators.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={condition.value}
                        onChange={(e) => updateCondition(ruleIndex, conditionIndex, { value: e.target.value })}
                        placeholder="Value..."
                        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        onClick={() => removeCondition(ruleIndex, conditionIndex)}
                        className="px-2 py-1 text-xs text-red-500 hover:text-red-700 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Add Rule Button */}
            <button
              onClick={addRule}
              className="w-full py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 hover:border-blue-400 dark:hover:border-gray-900 hover:text-gray-900 dark:hover:text-gray-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/10 dark:hover:to-purple-900/10 transition-all flex items-center justify-center space-x-2 font-semibold"
            >
              <PlusIcon className="h-5 w-5" />
              <span>{t('settings.workflowManagement.automationRules.addRule', 'Add New Rule')}</span>
            </button>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-5 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center space-x-2 font-semibold"
          >
            <SaveIcon className="h-4 w-4" />
            <span>{loading ? 'Saving...' : 'Save Rules'}</span>
          </button>
        </div>
      </div>

      {/* Task Template Editor Modal */}
      {showTaskTemplateEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-60 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-md">
                  <TaskIcon className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  📋 Create Task Template
                </h3>
              </div>
              <button
                onClick={() => setShowTaskTemplateEditor(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={editingTaskTemplate.name || ''}
                  onChange={(e) => setEditingTaskTemplate({ ...editingTaskTemplate, name: e.target.value })}
                  placeholder="e.g., Document Collection Reminder"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={editingTaskTemplate.description || ''}
                  onChange={(e) => setEditingTaskTemplate({ ...editingTaskTemplate, description: e.target.value })}
                  rows={3}
                  placeholder="Task description with {{variables}} support..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Deadline
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editingTaskTemplate.deadline_value || 1}
                    onChange={(e) => setEditingTaskTemplate({ ...editingTaskTemplate, deadline_value: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Unit
                  </label>
                  <select
                    value={editingTaskTemplate.deadline_unit || 'days'}
                    onChange={(e) => setEditingTaskTemplate({ ...editingTaskTemplate, deadline_unit: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={editingTaskTemplate.priority || 'medium'}
                  onChange={(e) => setEditingTaskTemplate({ ...editingTaskTemplate, priority: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowTaskTemplateEditor(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={createTaskTemplate}
                disabled={!editingTaskTemplate.name}
                className="px-5 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-semibold"
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EnhancedAutomationRuleBuilder